import React, { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import httpRequest from '../utils/request';
import buildNonFiscalTicketHtml from '../utils/ticketTemplate';
import { FaEye, FaMoneyBillWave, FaLock, FaUnlock, FaSearch } from 'react-icons/fa';

const CuentasCorrientes = () => {
  const { user } = useSelector((state) => state.auth);
  const { fiscalStatus, fiscalConfig } = useSelector((state) => state.fiscal || {});

  const emitterIsMonotributo = (() => {
    try {
      const fiscal = fiscalConfig || fiscalStatus;
      if (typeof fiscal?.discriminarIVA !== 'undefined') {
        if (fiscal.discriminarIVA === false) return true;
      }
      if (typeof fiscal?.regimenFiscal === 'string' && /mono/i.test(fiscal.regimenFiscal)) return true;
      const cond = fiscal?.condicionIVA ?? fiscal?.condicion ?? fiscal?.tipoResponsable;
      if (cond != null && Number(cond) === 6) return true;
      if (typeof cond === 'string' && /monotr/i.test(cond)) return true;
      return false;
    } catch (e) { return false; }
  })();
  const [cuentas, setCuentas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedCuenta, setSelectedCuenta] = useState(null);
  const [showMovimientos, setShowMovimientos] = useState(false);
  const [movimientos, setMovimientos] = useState([]);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [paymentMethod, setPaymentMethod] = useState('Efectivo');
  const [paymentNotes, setPaymentNotes] = useState('');

  useEffect(() => {
    fetchCuentas();
  }, []);

  const fetchCuentas = async () => {
    try {
      setLoading(true);
      const response = await httpRequest.get('/cuentas-corrientes');
      console.log('Response cuentas corrientes:', response.data);
      setCuentas(response.data.cuentas || response.data.data || []);
    } catch (error) {
      console.error('Error al cargar cuentas corrientes:', error);
      toast.error('Error al cargar cuentas corrientes');
    } finally {
      setLoading(false);
    }
  };

  const fetchMovimientos = async (cuentaId) => {
    try {
      const response = await httpRequest.get(`/cuentas-corrientes/${cuentaId}/movimientos`);
      setMovimientos(response.data.movimientos || []);
      setShowMovimientos(true);
    } catch (error) {
      console.error('Error al cargar movimientos:', error);
      toast.error('Error al cargar movimientos');
    }
  };

  const handleVerMovimientos = (cuenta) => {
    setSelectedCuenta(cuenta);
    fetchMovimientos(cuenta._id);
  };

  const handleOpenPayment = (cuenta) => {
    setSelectedCuenta(cuenta);
    setPaymentAmount('');
    setPaymentMethod('Efectivo');
    setPaymentNotes('');
    setShowPaymentModal(true);
  };

  const handleRegisterPayment = async () => {
    if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
      toast.error('Ingrese un monto válido');
      return;
    }

    try {
      await httpRequest.post(`/cuentas-corrientes/${selectedCuenta._id}/pago`, {
        amount: parseFloat(paymentAmount),
        paymentMethod,
        notes: paymentNotes
      });
      toast.success('✅ Pago registrado correctamente');
      setShowPaymentModal(false);
      fetchCuentas();
      if (showMovimientos) {
        fetchMovimientos(selectedCuenta._id);
      }
    } catch (error) {
      console.error('Error al registrar pago:', error);
      const errorMsg = error.response?.data?.message || 'Error al registrar el pago';
      toast.error(`❌ ${errorMsg}`);
    }
  };

  const handleToggleBlock = async (cuenta) => {
    const isBlocked = cuenta.status === 'blocked';
    const action = isBlocked ? 'desbloquear' : 'bloquear';
    const endpoint = isBlocked ? 'unblock' : 'block';
    
    const confirm = window.confirm(
      `¿Está seguro que desea ${action} la cuenta corriente de ${cuenta.customerId?.razonSocial || 'este cliente'}?`
    );

    if (!confirm) return;

    try {
      await httpRequest.put(`/cuentas-corrientes/${cuenta._id}/${endpoint}`);
      toast.success(`✅ Cuenta ${isBlocked ? 'desbloqueada' : 'bloqueada'} correctamente`);
      fetchCuentas();
    } catch (error) {
      console.error('Error al cambiar estado:', error);
      toast.error(error.response?.data?.message || 'Error al cambiar estado de la cuenta');
    }
  };

  const filteredCuentas = cuentas.filter((cuenta) => {
    const customerName = cuenta.customer?.razonSocial?.toLowerCase() || '';
    const search = searchTerm.toLowerCase();
    return customerName.includes(search);
  });

  const _formatDate = (date) => {
    if (!date) return '-';
    return new Date(date).toLocaleDateString('es-AR');
  };

  const formatCurrency = (amount) => {
    return `$${(amount || 0).toFixed(2)}`;
  };

  const getTipoMovimientoLabel = (tipo) => {
    const labels = {
      sale: '💳 Venta',
      payment: '💰 Pago',
      adjustment: '⚙️ Ajuste'
    };
    return labels[tipo] || tipo;
  };

  const handleViewTicket = async (saleId) => {
    try {
      // Obtener la venta
      const saleResp = await httpRequest.get(`/sales/${saleId}`);
      const sale = saleResp.data;

      // Intentar obtener factura asociada (filtra por saleId)
      let invoice = null;
      try {
        const invResp = await httpRequest.get(`/invoices?saleId=${saleId}`);
        const list = Array.isArray(invResp.data) ? invResp.data : [];
        if (list.length > 0) {
          // Tomar la primera autorizada si existe, sino la primera
          invoice = list.find(i => i.estado === 'autorizada') || list[0];
        }
      } catch (e) {
        console.warn('No se pudo obtener facturas para la venta:', e);
      }

      // Si hay factura autorizada intentar abrir PDF (on-demand reprint endpoint)
      if (invoice && invoice.estado === 'autorizada') {
        // Intentar descargar el PDF autenticado vía XHR (Bearer/cookies) para evitar 401 en ventana nueva
        try {
          const pdfResp = await httpRequest.get(`/invoices/${invoice._id}/pdf?inline=true`, { responseType: 'blob' });
          const blob = new Blob([pdfResp.data], { type: 'application/pdf' });
          const url = URL.createObjectURL(blob);
          window.open(url, '_blank');
          return;
        } catch (e) {
          console.warn('Fallo descarga PDF autenticada, fallback a abrir URL directa:', e);
          const directUrl = `/api/invoices/${invoice._id}/pdf?inline=true`;
          window.open(directUrl, '_blank');
          return;
        }
      }

      // Fallback: construir ticket NO fiscal
      const productsResp = await httpRequest.get('/product/all-products');
      const products = productsResp.data || [];
      const logoData = typeof window !== 'undefined' ? localStorage.getItem('ticketLogoData') : null;
      const leftName = typeof window !== 'undefined' ? localStorage.getItem('ticketLeftName') : null;

      let customerData = null;
      if (sale.customer) {
        try {
          const custResp = await httpRequest.get(`/customers/${sale.customer}`);
          customerData = custResp.data;
        } catch (e) {
          console.warn('No se pudo obtener datos del cliente:', e);
        }
      }

      const normalizedSale = {
        items: sale.items,
        total: sale.total,
        saleNumber: sale.saleNumber,
        createdAt: sale.date || sale.createdAt || new Date().toISOString(),
        date: sale.date || sale.createdAt || new Date().toISOString()
      };

      const html = buildNonFiscalTicketHtml(normalizedSale, {
        products,
        settingsLogoData: logoData,
        settingsLeftName: leftName,
        customerData: customerData ? {
          razonSocial: customerData.razonSocial || customerData.name,
          cuit: customerData.cuit,
          tipoCliente: customerData.tipoCliente
        } : null,
        emitterIsMonotributo
      });

      const w = window.open('', '_blank', 'toolbar=0,location=0,menubar=0,width=380,height=800');
      if (!w) {
        toast.error('No se pudo abrir la ventana. Verifique el popup blocker.');
        return;
      }
      w.document.open();
      w.document.write(html);
      w.document.close();
      setTimeout(() => { w.print(); }, 500);
    } catch (error) {
      console.error('Error al ver ticket:', error);
      toast.error('Error al cargar el comprobante');
    }
  };

  return (
    <div style={{ padding: '20px', maxWidth: '1400px', margin: '0 auto' }}>
      <div style={{ marginBottom: '30px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '15px' }}>
        <h1 style={{ fontSize: '28px', fontWeight: 'bold', margin: 0 }}>📒 Cuentas Corrientes</h1>
        <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
          <div style={{ position: 'relative' }}>
            <FaSearch style={{ position: 'absolute', left: '12px', top: '50%', transform: 'translateY(-50%)', color: '#999' }} />
            <input
              type="text"
              placeholder="Buscar cliente..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{
                padding: '10px 10px 10px 38px',
                borderRadius: '6px',
                border: '1px solid #ddd',
                fontSize: '14px',
                width: '250px'
              }}
            />
          </div>
        </div>
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: '40px' }}>Cargando...</div>
      ) : filteredCuentas.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '40px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
          <p style={{ fontSize: '16px', color: '#666' }}>
            {searchTerm ? 'No se encontraron cuentas corrientes' : 'No hay cuentas corrientes registradas'}
          </p>
        </div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', backgroundColor: '#fff', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', borderRadius: '8px', overflow: 'hidden' }}>
            <thead>
              <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                <th style={{ padding: '15px', textAlign: 'left', fontWeight: '600', fontSize: '14px' }}>Cliente</th>
                <th style={{ padding: '15px', textAlign: 'right', fontWeight: '600', fontSize: '14px' }}>Saldo</th>
                <th style={{ padding: '15px', textAlign: 'right', fontWeight: '600', fontSize: '14px' }}>Deuda Vencida</th>
                <th style={{ padding: '15px', textAlign: 'right', fontWeight: '600', fontSize: '14px' }}>Límite Crédito</th>
                <th style={{ padding: '15px', textAlign: 'center', fontWeight: '600', fontSize: '14px' }}>Estado</th>
                <th style={{ padding: '15px', textAlign: 'center', fontWeight: '600', fontSize: '14px' }}>Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filteredCuentas.map((cuenta) => {
                const saldoColor = cuenta.balance < 0 ? '#dc3545' : cuenta.balance > 0 ? '#28a745' : '#6c757d';
                const deudaVencida = cuenta.overdueDebt || 0;
                const disponible = (cuenta.creditLimit || 0) - Math.abs(cuenta.balance || 0);

                return (
                  <tr key={cuenta._id} style={{ borderBottom: '1px solid #dee2e6' }}>
                    <td style={{ padding: '15px' }}>
                      <div style={{ fontWeight: '600', fontSize: '15px' }}>{cuenta.customerId?.razonSocial || 'Sin nombre'}</div>
                      <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                        Plazo: {cuenta.paymentDueDays || 0} días
                      </div>
                    </td>
                    <td style={{ padding: '15px', textAlign: 'right', fontWeight: '600', color: saldoColor, fontSize: '16px' }}>
                      {formatCurrency(cuenta.balance)}
                    </td>
                    <td style={{ padding: '15px', textAlign: 'right' }}>
                      <span style={{ color: deudaVencida > 0 ? '#dc3545' : '#6c757d', fontWeight: deudaVencida > 0 ? '600' : 'normal' }}>
                        {formatCurrency(deudaVencida)}
                      </span>
                    </td>
                    <td style={{ padding: '15px', textAlign: 'right' }}>
                      <div style={{ fontSize: '14px' }}>{formatCurrency(cuenta.creditLimit)}</div>
                      <div style={{ fontSize: '12px', color: disponible < 0 ? '#dc3545' : '#28a745', marginTop: '2px' }}>
                        Disp: {formatCurrency(disponible)}
                      </div>
                    </td>
                    <td style={{ padding: '15px', textAlign: 'center' }}>
                      <span style={{
                        padding: '4px 12px',
                        borderRadius: '12px',
                        fontSize: '12px',
                        fontWeight: '600',
                        backgroundColor: cuenta.status === 'active' ? '#d4edda' : '#f8d7da',
                        color: cuenta.status === 'active' ? '#155724' : '#721c24'
                      }}>
                        {cuenta.status === 'active' ? 'Activa' : 'Bloqueada'}
                      </span>
                    </td>
                    <td style={{ padding: '15px', textAlign: 'center' }}>
                      <div style={{ display: 'flex', gap: '8px', justifyContent: 'center' }}>
                        <button
                          onClick={() => handleVerMovimientos(cuenta)}
                          style={{
                            padding: '8px 12px',
                            backgroundColor: '#17a2b8',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px'
                          }}
                          title="Ver movimientos"
                        >
                          <FaEye /> Ver
                        </button>
                        <button
                          onClick={() => handleOpenPayment(cuenta)}
                          style={{
                            padding: '8px 12px',
                            backgroundColor: '#28a745',
                            color: '#fff',
                            border: 'none',
                            borderRadius: '6px',
                            cursor: 'pointer',
                            fontSize: '13px',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '5px'
                          }}
                          title="Registrar pago"
                          disabled={cuenta.status === 'blocked'}
                        >
                          <FaMoneyBillWave /> Pagar
                        </button>
                        {(user.role === 'owner' || user.role === 'admin') && (
                          <button
                            onClick={() => handleToggleBlock(cuenta)}
                            style={{
                              padding: '8px 12px',
                              backgroundColor: cuenta.status === 'active' ? '#dc3545' : '#ffc107',
                              color: '#fff',
                              border: 'none',
                              borderRadius: '6px',
                              cursor: 'pointer',
                              fontSize: '13px',
                              display: 'flex',
                              alignItems: 'center',
                              gap: '5px'
                            }}
                            title={cuenta.status === 'active' ? 'Bloquear' : 'Desbloquear'}
                          >
                            {cuenta.status === 'active' ? <FaLock /> : <FaUnlock />}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* Modal de Movimientos */}
      {showMovimientos && selectedCuenta && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
          }}
          onClick={() => setShowMovimientos(false)}
        >
          <div
            style={{
              backgroundColor: '#fff',
              borderRadius: '12px',
              padding: '30px',
              maxWidth: '900px',
              width: '90%',
              maxHeight: '80vh',
              overflow: 'auto',
              boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h2 style={{ fontSize: '24px', fontWeight: 'bold', margin: 0 }}>
                Movimientos de {selectedCuenta.customer?.razonSocial}
              </h2>
              <button
                onClick={() => setShowMovimientos(false)}
                style={{
                  padding: '8px 16px',
                  backgroundColor: '#6c757d',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Cerrar
              </button>
            </div>

            <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '15px' }}>
                <div>
                  <div style={{ fontSize: '12px', color: '#666' }}>Saldo Actual</div>
                  <div style={{ fontSize: '20px', fontWeight: 'bold', color: selectedCuenta.balance < 0 ? '#dc3545' : '#28a745' }}>
                    {formatCurrency(selectedCuenta.balance)}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#666' }}>Deuda Total</div>
                  <div style={{ fontSize: '20px', fontWeight: 'bold' }}>
                    {formatCurrency(selectedCuenta.totalDebt)}
                  </div>
                </div>
                <div>
                  <div style={{ fontSize: '12px', color: '#666' }}>Deuda Vencida</div>
                  <div style={{ fontSize: '20px', fontWeight: 'bold', color: selectedCuenta.overdueDebt > 0 ? '#dc3545' : '#6c757d' }}>
                    {formatCurrency(selectedCuenta.overdueDebt)}
                  </div>
                </div>
              </div>
            </div>

            {movimientos.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '40px', color: '#666' }}>
                No hay movimientos registrados
              </div>
            ) : (
              <div style={{ overflowX: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #dee2e6' }}>
                      <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px' }}>Fecha y Hora</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px' }}>Tipo</th>
                      <th style={{ padding: '12px', textAlign: 'center', fontSize: '13px' }}>N° Comprobante</th>
                      <th style={{ padding: '12px', textAlign: 'right', fontSize: '13px' }}>Monto</th>
                      <th style={{ padding: '12px', textAlign: 'left', fontSize: '13px' }}>Descripción</th>
                      <th style={{ padding: '12px', textAlign: 'right', fontSize: '13px' }}>Saldo Después</th>
                      <th style={{ padding: '12px', textAlign: 'center', fontSize: '13px' }}>Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {movimientos.map((mov) => (
                      <tr key={mov._id} style={{ borderBottom: '1px solid #dee2e6' }}>
                        <td style={{ padding: '12px', fontSize: '13px' }}>
                          <div>{new Date(mov.createdAt || mov.date).toLocaleDateString('es-AR')}</div>
                          <div style={{ fontSize: '11px', color: '#666' }}>
                            {new Date(mov.createdAt || mov.date).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}
                          </div>
                        </td>
                        <td style={{ padding: '12px', fontSize: '13px' }}>{getTipoMovimientoLabel(mov.type)}</td>
                        <td style={{ padding: '12px', textAlign: 'center', fontSize: '13px' }}>
                          {mov.invoiceNumber ? (
                            <span style={{ fontWeight: '600', color: '#17a2b8' }}>
                              FC {mov.invoiceNumber}
                            </span>
                          ) : mov.receiptNumber ? (
                            <span style={{ fontWeight: '600', color: '#ffc107' }}>
                              Ticket {mov.receiptNumber}
                            </span>
                          ) : (
                            <span style={{ color: '#999' }}>-</span>
                          )}
                        </td>
                        <td style={{
                          padding: '12px',
                          textAlign: 'right',
                          fontSize: '14px',
                          fontWeight: '600',
                          color: mov.type === 'payment' ? '#28a745' : '#dc3545'
                        }}>
                          {mov.type === 'payment' ? '+' : '-'}{formatCurrency(Math.abs(mov.amount))}
                        </td>
                        <td style={{ padding: '12px', fontSize: '13px' }}>
                          {mov.description || mov.notes || '-'}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'right', fontSize: '13px', fontWeight: '600' }}>
                          {formatCurrency(mov.balanceAfter)}
                        </td>
                        <td style={{ padding: '12px', textAlign: 'center' }}>
                          {mov.type === 'sale' && mov.saleId && (
                            <button
                              onClick={() => handleViewTicket(mov.saleId)}
                              style={{
                                padding: '6px 12px',
                                backgroundColor: '#17a2b8',
                                color: '#fff',
                                border: 'none',
                                borderRadius: '4px',
                                cursor: 'pointer',
                                fontSize: '12px'
                              }}
                              title="Ver ticket de venta"
                            >
                              🧾 Ver
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Modal de Registro de Pago */}
      {showPaymentModal && selectedCuenta && (
        <div
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(0,0,0,0.5)',
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            zIndex: 1000
          }}
          onClick={() => setShowPaymentModal(false)}
        >
          <div
            style={{
              backgroundColor: '#fff',
              borderRadius: '12px',
              padding: '30px',
              maxWidth: '500px',
              width: '90%',
              boxShadow: '0 10px 40px rgba(0,0,0,0.2)'
            }}
            onClick={(e) => e.stopPropagation()}
          >
            <h2 style={{ fontSize: '24px', fontWeight: 'bold', marginBottom: '20px' }}>
              💰 Registrar Pago
            </h2>

            <div style={{ marginBottom: '20px', padding: '15px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
              <div style={{ fontSize: '14px', color: '#666' }}>Cliente</div>
              <div style={{ fontSize: '18px', fontWeight: 'bold' }}>{selectedCuenta.customer?.razonSocial}</div>
              <div style={{ fontSize: '14px', color: '#666', marginTop: '8px' }}>Saldo actual: <span style={{ fontWeight: 'bold', color: '#dc3545' }}>{formatCurrency(selectedCuenta.balance)}</span></div>
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '600' }}>
                Monto del Pago *
              </label>
              <input
                type="number"
                step="0.01"
                value={paymentAmount}
                onChange={(e) => setPaymentAmount(e.target.value)}
                placeholder="0.00"
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '6px',
                  border: '1px solid #ddd',
                  fontSize: '16px'
                }}
                autoFocus
              />
            </div>

            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '600' }}>
                Método de Pago
              </label>
              <select
                value={paymentMethod}
                onChange={(e) => setPaymentMethod(e.target.value)}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '6px',
                  border: '1px solid #ddd',
                  fontSize: '14px'
                }}
              >
                <option value="Efectivo">💵 Efectivo</option>
                <option value="Tarjeta">💳 Tarjeta</option>
                <option value="Transferencia">🏦 Transferencia</option>
              </select>
            </div>

            <div style={{ marginBottom: '25px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '14px', fontWeight: '600' }}>
                Notas (opcional)
              </label>
              <textarea
                value={paymentNotes}
                onChange={(e) => setPaymentNotes(e.target.value)}
                placeholder="Observaciones sobre el pago..."
                rows={3}
                style={{
                  width: '100%',
                  padding: '12px',
                  borderRadius: '6px',
                  border: '1px solid #ddd',
                  fontSize: '14px',
                  resize: 'vertical'
                }}
              />
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setShowPaymentModal(false)}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#6c757d',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px'
                }}
              >
                Cancelar
              </button>
              <button
                onClick={handleRegisterPayment}
                style={{
                  padding: '12px 24px',
                  backgroundColor: '#28a745',
                  color: '#fff',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '14px',
                  fontWeight: '600'
                }}
              >
                ✅ Registrar Pago
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CuentasCorrientes;
